<script lang="ts" setup>
const { getContinentAttributes, getCountryAttributes, getMetaAttributes } = useApiAttributes()
const { t } = useI18n()
const api = useApi()

const { data: response, execute, status } = useAsyncData(
  'doc:locations-search-states',
  () => api.locations.states.fetch(),
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/locations/states',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.page'),
    name: 'page',
    optional: true,
    type: 'number',
  },
  {
    description: t('attributes.search'),
    name: 'search',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.size'),
    name: 'size',
    optional: true,
    type: 'number',
  },
]

const responseAttributes: ApiAttribute[] = [
  {
    description: t('response.states'),
    name: 'states',
    type: 'array',

    attributes: [
      {
        description: t('response.code'),
        name: 'code',
        parent: 'state',
        type: 'string',
      },
      {
        attributes: getContinentAttributes({ parent: 'state' }),
        description: t('response.continent'),
        name: 'continent',
        parent: 'state',
        type: 'object',
      },
      {
        attributes: getCountryAttributes({ parent: 'state' }),
        description: t('response.country'),
        name: 'country',
        parent: 'state',
        type: 'object',
      },
      {
        description: t('response.latitude'),
        name: 'latitude',
        parent: 'state',
        type: 'string',
      },
      {
        description: t('response.longitude'),
        name: 'longitude',
        parent: 'state',
        type: 'string',
      },
      {
        description: t('response.name'),
        name: 'name',
        parent: 'state',
        type: 'string',
      },
    ],
  },
  getMetaAttributes(),
]

async function onVisibilityChange(visibility: boolean) {
  if (visibility && !response.value) {
    await execute()
  }
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "page": "The page to fetch (default to 1).",
      "search": "An additional search query applied to the state name.",
      "size": "The number of states to be returned (between 1 and 20). Default to 10."
    },
    "response": {
      "code": "The state code.",
      "continent": "The state's continent.",
      "country": "The state's country.",
      "latitude": "The state's latitude.",
      "longitude": "The state's longitude.",
      "name": "The state's name.",
      "states": "The states that match your conditions."
    }
  },
  "es": {
    "attributes": {
      "page": "La página a recuperar (por defecto 1).",
      "search": "Una consulta de búsqueda adicional aplicada al nombre del estado.",
      "size": "El número de estados a devolver (entre 1 y 20). Por defecto 10."
    },
    "response": {
      "code": "El código del estado.",
      "continent": "El continente del estado.",
      "country": "El país del estado.",
      "latitude": "La latitud del estado.",
      "longitude": "La longitud del estado.",
      "name": "El nombre del estado.",
      "states": "Los estados que coinciden con tus criterios."
    }
  },
  "fr": {
    "attributes": {
      "page": "La page à récupérer (par défaut 1).",
      "search": "Une recherche additionnelle appliquée au nom de la région.",
      "size": "Le nombre d'états à retourner (entre 1 et 20). Par défaut 10."
    },
    "response": {
      "code": "Le code de la région.",
      "continent": "Le continent de la région.",
      "country": "Le pays de la région.",
      "latitude": "La latitude de la région.",
      "longitude": "La longitude de la région.",
      "name": "Le nom de la région.",
      "states": "Les états qui correspondent à vos critères."
    }
  }
}
</i18n>
